import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';

// UI
import { Breadcrumbs } from '@virginexperiencedays/components-v2/src/navigation/Breadcrumbs';
import { Divider } from '@virginexperiencedays/components-v2/src/layout/Divider';
import { Article } from '@virginexperiencedays/components-v2/src/cms/Article';
import { Content } from '@virginexperiencedays/components-v2/src/cms/Content';
import { PostHeroPanel } from '@virginexperiencedays/components-v2/src/pages/blog/PostHeroPanel';
import { PostBody } from '@virginexperiencedays/components-v2/src/pages/blog/PostBody';
import { BlogPostAuthor } from '@virginexperiencedays/components-v2/src/pages/blog/BlogPostAuthor';
import { Newsletter } from '@virginexperiencedays/components-v2/src/layout/Newsletter';
import { RelatedPosts } from '@virginexperiencedays/components-v2/src/pages/blog/RelatedPosts';
// hooks
import { useTracking } from '../tracking/useTracking';

// types
import type { BlogPostPageType } from '@virginexperiencedays/cms';
import type { ProductMapType } from '@virginexperiencedays/products';

// SEO
import postPageStructuredData from '../SEO/StructuredData/postPage';
import SEO from '../SEO/SEO';
import { GLOBAL_SETTINGS } from '../tracking/gtm/consts';

// utils
import DynamicYield from '../DynamicYield/DynamicYield';
import { DyContextPageType } from '@virginexperiencedays/dy';
import { fallbackImage } from '../../utils/fallbackImage';
import { SIGNUP_SOURCE, submit } from '../../utils/newsletter';
import { buildMetaRobotsContent } from '../SEO/utils/buildMetaRobotsContent';
import { pageView as trackFbPageView } from '../tracking/fb/events';

type BlogPostPageProps = {
  post: BlogPostPageType;
  products: ProductMapType;
  commonCrumbs: { name: string; path?: string }[];
};

function BlogPostPage({ post, products, commonCrumbs }: BlogPostPageProps) {
  const { coverimage, title, body, author } = post;
  const datePublished = post.date
    ? new Date(post.date).toISOString()
    : post._meta?.firstPublicationDate;

  const relatedBlogPosts = post?.related_blog_posts?.filter(
    ({ blog_post_page }) => !!blog_post_page
  );
  const router = useRouter();
  const pathname = router.pathname;

  useTracking({
    legacy: { pageType: 'Blog Post', pageName: title },
    gtm: { initial: [GLOBAL_SETTINGS, { ecommerce: null }] },
    fb: () => {
      trackFbPageView({
        name: title,
      });
    },
  });

  return (
    <div>
      <Head>
        {process.env.NEXT_PUBLIC_HOME_LINK ? (
          <link
            rel="canonical"
            href={`${process.env.NEXT_PUBLIC_HOME_LINK}/experience-blog/${post._meta?.uid}`}
          />
        ) : null}
        <link rel="preconnect" href="https://images.prismic.io/" />
      </Head>
      <SEO
        {...post}
        og_image={post.og_image ?? coverimage?.OpenGraph ?? fallbackImage.OpenGraph}
        meta_title={post.meta_title ?? `${title} | Virgin Experience Days`}
        meta_description={
          post.meta_description ??
          `Read on to discover tips, tricks and inspiration on ${title}, from Virgin Experience Days.`
        }
        structuredData={postPageStructuredData({
          name: title,
          urlSlug: post._meta?.uid,
          baseUrl: `experience-blog`,
          headline: title,
          datePublished,
          dateModified: post._meta?.lastPublicationDate,
          author: {
            name: author?.name,
            image: {
              url: author?.avatar?.url,
              width: author?.avatar?.dimensions.width,
              height: author?.avatar?.dimensions.height,
            },
          },
          image: {
            url: coverimage?.url ?? fallbackImage.url,
            width: coverimage?.dimensions.width ?? fallbackImage.dimensions.width,
            height: coverimage?.dimensions.height ?? fallbackImage.dimensions.height,
          },
          breadcrumbs: commonCrumbs
            .concat(
              [
                !!post.parent_link?._meta?.uid && {
                  name: `${post.parent_link?.title ?? 'Categories'}`,
                  path: `${post.parent_link?._meta?.uid}`,
                },
                {
                  name: `${title}`,
                  path: `/experience-blog/${post._meta?.uid}`,
                },
              ].filter((crumb) => crumb)
            )
            .map((crumb) => ({ name: crumb.name, url: crumb.path })),
        })}
        meta_robots={buildMetaRobotsContent(post?.meta_robots)}
      />
      <DynamicYield type={DyContextPageType.OTHER} data={pathname ?? []} />
      <Content className="py-8">
        <Breadcrumbs
          crumbs={commonCrumbs.concat(
            [
              !!post.parent_link && {
                name: `${post.parent_link?.title ?? 'Categories'}`,
                path: `${post.parent_link?._meta?.uid}`,
              },
              {
                name: `${title}`,
              },
            ].filter((crumb) => crumb)
          )}
        />
        <PostHeroPanel
          className="mb-20 mt-10 items-center"
          title={title}
          imgUrl={`${coverimage?.Post.url}` as string}
          imgAlt={coverimage?.alt ?? title ?? ''}
          description={post?.description}
          subDescription={post?.sub_description}
          imageObject={coverimage?.Post}
        />
        {body && (
          <Article>
            <PostBody body={body} location_title={title} products={products} />
          </Article>
        )}
        <Article>
          <div className="py-10">
            <Divider variant="grey" />
          </div>
        </Article>
        {!!author && (
          <Article>
            <BlogPostAuthor className="mb-6" author={author} />
          </Article>
        )}
        {relatedBlogPosts && relatedBlogPosts.length > 0 && (
          <>
            <Divider variant="dots" className="py-10" />
            <RelatedPosts
              posts={relatedBlogPosts.map(({ blog_post_page }) => blog_post_page)}
              title="Related Blog Posts"
            />
          </>
        )}
      </Content>
      <Content className="py-8">
        <Divider variant="grey" />
        <Newsletter
          onSubmit={submit}
          signUpSource={SIGNUP_SOURCE.blogPost}
          roundedLeftButton={false}
        />
      </Content>
    </div>
  );
}

export default BlogPostPage;
